import type { StatusVariant } from '../components/ui/StatusBadge';
import type { WasteFlag, WasteReviewItem, WasteThreshold, WasteThresholdMode } from '../types/waste';
import { looksLikeThousandsSeparator, parseDecimalInput } from './wasteCapture';

// F1 · textos PUROS de la bandeja «Mermas de hoy» (gerente) y de «mis mermas». Sin jerga técnica.

const money = (value: string | number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value));
const percent = (value: number) => `${value.toLocaleString('es-MX', { maximumFractionDigits: 1 })} %`;

export function formatWasteQuantity(quantity: string, unitSymbol: string): string {
  return `${new Intl.NumberFormat('es-MX', { maximumFractionDigits: 4 }).format(Number(quantity))} ${unitSymbol}`;
}

/** Hora en la zona de la TIENDA donde pasó (no la del teléfono que la mira). */
export function formatWasteTime(occurredAt: string, timeZone: string): string {
  return new Intl.DateTimeFormat('es-MX', { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(occurredAt));
}

/** «Merma de $240.00 · 18 % de lo que había». Si falta un número, no se inventa. */
export function wasteMeasureText(item: Pick<WasteReviewItem, 'value' | 'percentOfPriorStock'>): string {
  const parts = [item.value === null ? 'Sin costo registrado' : `Merma de ${money(item.value)}`];
  if (item.percentOfPriorStock !== null) parts.push(`${percent(item.percentOfPriorStock)} de lo que había`);
  return parts.join(' · ');
}

export const WASTE_FLAG_LABELS: Readonly<Record<WasteFlag, string>> = {
  LARGE: 'Merma grande',
  STAFF_CONSUMPTION: 'Consumo del personal',
};

export function wasteFlagBadges(flags: WasteFlag[]): Array<{ flag: WasteFlag; label: string; variant: StatusVariant }> {
  return flags.map((flag) => ({ flag, label: WASTE_FLAG_LABELS[flag], variant: flag === 'LARGE' ? 'danger' : 'warning' }));
}

/** Cómo se decide «merma grande», en palabras. */
export function thresholdSummary(threshold: WasteThreshold): string {
  const rule = threshold.mode === 'AMOUNT'
    ? `Una merma se marca como grande desde ${money(threshold.value)} (si el insumo no tiene costo registrado, desde ${percent(threshold.uncostedPercentage)} de lo que había).`
    : `Una merma se marca como grande desde ${percent(threshold.value)} de lo que había del insumo.`;
  return threshold.isDefault ? `${rule} Es la sugerencia inicial; el dueño puede cambiarla.` : rule;
}

export const MAX_THRESHOLD_AMOUNT = 1_000_000;

type NumberProblem = 'INVALID' | 'THOUSANDS' | 'DECIMALS';

/** Mismo intérprete que la cantidad de merma («1,000» no se lee como 1), con 2 decimales. */
function thresholdNumberProblem(text: string): NumberProblem | null {
  const trimmed = text.trim();
  if (looksLikeThousandsSeparator(trimmed)) return 'THOUSANDS';
  if (/^\d+[.,]\d{3,}$/.test(trimmed)) return 'DECIMALS';
  const value = parseDecimalInput(trimmed, 2);
  return value === null || value <= 0 ? 'INVALID' : null;
}

/**
 * Por qué no se puede guardar el umbral (null = listo). Solo se revisa lo que está a la vista: el
 * porcentaje para insumos sin costo solo aparece (y solo se valida) en modo pesos.
 */
export function wasteThresholdBlocker(mode: WasteThresholdMode, valueText: string, uncostedText: string): string | null {
  const valueProblem = thresholdNumberProblem(valueText);
  if (valueProblem === 'THOUSANDS') return 'Escríbelo sin separador de miles: 1000, no 1,000.';
  if (valueProblem === 'DECIMALS') return 'Usa como máximo 2 decimales.';
  if (valueProblem) return 'Escribe un número mayor a 0.';
  const value = parseDecimalInput(valueText, 2)!;
  if (mode === 'AMOUNT' && value > MAX_THRESHOLD_AMOUNT) return 'El monto va hasta $1,000,000.';
  if (mode === 'PERCENTAGE' && value > 100) return 'El porcentaje debe ser mayor a 0 y hasta 100.';
  if (mode === 'AMOUNT') {
    const uncostedProblem = thresholdNumberProblem(uncostedText);
    if (uncostedProblem === 'DECIMALS') return 'El porcentaje para insumos sin costo lleva como máximo 2 decimales.';
    if (uncostedProblem || parseDecimalInput(uncostedText, 2)! > 100) return 'El porcentaje para insumos sin costo debe ser mayor a 0 y hasta 100.';
  }
  return null;
}

/**
 * El porcentaje de respaldo que se manda al guardar. Se manda siempre, para no perderlo al cambiar de
 * modo; si el campo no está a la vista (modo porcentaje) y lo escrito no sirve, se conserva el guardado.
 */
export function thresholdUncostedToSend(mode: WasteThresholdMode, uncostedText: string, stored: number): number {
  const typed = parseDecimalInput(uncostedText, 2);
  if (typed !== null && typed > 0 && typed <= 100) return typed;
  return mode === 'AMOUNT' ? Number.NaN : stored;
}

/** Motivos rápidos para anular (el gerente también puede escribir el suyo). */
export const WASTE_REVERSAL_REASONS = ['Se registró dos veces', 'La cantidad estaba mal', 'Era otro insumo', 'No fue merma'] as const;

/** Primero lo que hay que revisar (vigente con señales), luego lo vigente, al final lo anulado. */
export function sortWasteForReview<T extends Pick<WasteReviewItem, 'flags' | 'reversed' | 'occurredAt'>>(items: T[]): T[] {
  const rank = (item: T) => (item.reversed ? 2 : item.flags.length ? 0 : 1);
  return [...items].sort((a, b) => rank(a) - rank(b) || b.occurredAt.localeCompare(a.occurredAt));
}
